import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { T, FONT, MONO, radius, tint, alpha } from './poyais-ui/theme';
import { fmt_k } from './poyais-ui/format';
import { useIsMobile } from './poyais-ui/hooks';
import { Card, Grid, Eyebrow, Badge, Button } from './poyais-ui/primitives';
import { Icon } from './poyais-ui/icons';
import { exportCsv } from './poyais-ui/export';
import { useLang, addTranslations } from './i18n';
import PoyaisSpinner from './PoyaisSpinner';

addTranslations({
  'X Ads Bağlı Değil': 'X Ads Not Connected',
  'Ayarlar sayfasından X (Twitter) hesabınızı bağlayın.': 'Connect your X (Twitter) account on the Settings page.',
  'Ayarlara Git': 'Go to Settings',
  'Veri alınamadı': 'Could not load data',
  'Hata': 'Error',
  'HARCAMA': 'SPEND', 'TIKLAMA': 'CLICKS', 'GÖSTERİM': 'IMPRESSIONS', 'DURUM': 'STATUS',
  'ETKİLEŞİM': 'ENGAGEMENTS', 'KAMPANYA SAYISI': 'CAMPAIGN COUNT',
  'önceki döneme göre': 'vs. previous period',
  'Kampanyalar': 'Campaigns',
  'kampanya': 'campaigns',
  'Yeni Kampanya': 'New Campaign',
  'Dışa Aktar': 'Export',
  'KAMPANYA': 'CAMPAIGN', 'GÜNLÜK BÜTÇE': 'DAILY BUDGET',
  'Bu dönem için kampanya yok.': 'No campaigns for this period.',
  'Aktif': 'Active', 'Durduruldu': 'Paused',
});

const API = '/api/v1';

function fmtN(v: number) { return Number(v || 0).toLocaleString('tr-TR'); }
function fmtPct(v: number) { return `%${Number(v || 0).toFixed(2)}`; }

function Loader() { return <div className="py-pageload"><PoyaisSpinner size={38} pad={0}/></div>; }

function DisconnectedState() { const { t } = useLang(); return (<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 320, gap: 14, fontFamily: FONT }}><div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>{t('X Ads Bağlı Değil')}</div><div style={{ fontSize: 13, color: T.textDim }}>{t('Ayarlar sayfasından X (Twitter) hesabınızı bağlayın.')}</div><Button variant="solid" color={T.accent} onClick={() => window.dispatchEvent(new CustomEvent('poyais-nav', { detail: 'settings' }))}>{t('Ayarlara Git')}</Button></div>); }

// --- GENEL BAKIŞ ---
function OverviewTab({ clientId, start, end }: { clientId: number; start?: string; end?: string }) {
  const { t } = useLang();
  const isMobile = useIsMobile();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

  useEffect(() => {
    setLoading(true); setError(null); setData(null);
    axios.get(`${API}/x-ads/${clientId}/overview`, { params: { start_date: start || monthStart, end_date: end || today } })
      .then(r => { setData(r.data); setLoading(false); })
      .catch(e => { setError(e.response?.data?.detail || t('Veri alınamadı')); setLoading(false); });
  }, [clientId, start, end]);

  if (loading) return <Loader />;
  if (error) return <div style={{ background: tint(T.red, 8), border: `1px solid ${alpha(T.red, 25)}`, borderRadius: radius.lg, padding: '20px 24px', fontFamily: FONT }}><div style={{ fontSize: 13.5, fontWeight: 800, color: T.redText, marginBottom: 6 }}>{t('Hata')}</div><div style={{ fontSize: 12, color: T.textMid }}>{error}</div></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontFamily: FONT }}>
      <Grid min={isMobile ? 130 : 150} gap={isMobile ? 8 : 14}>
        {[
          { label: t('HARCAMA'), value: fmt_k(data?.spend || 0), color: T.accent },
          { label: t('TIKLAMA'), value: fmtN(data?.clicks), color: T.blue },
          { label: t('GÖSTERİM'), value: fmtN(data?.impressions), color: T.textDim },
          { label: 'CTR', value: fmtPct(data?.ctr), color: T.textDim },
          { label: t('ETKİLEŞİM'), value: fmtN(data?.engagements), color: T.textDim },
          { label: t('KAMPANYA SAYISI'), value: fmtN(data?.campaigns_count), color: T.textDim },
        ].map((k, i) => (
          <Card key={i} pad={18}>
            <Eyebrow color={k.color}>{k.label}</Eyebrow>
            <div style={{ fontSize: 22, fontWeight: 800, color: T.text, fontFamily: MONO, fontVariantNumeric: 'tabular-nums', marginTop: 8 }}>{k.value}</div>
          </Card>
        ))}
      </Grid>
    </div>
  );
}

// --- KAMPANYALAR ---
function CampaignsTab({ clientId, start, end }: { clientId: number; start?: string; end?: string }) {
  const { t } = useLang();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/x-ads/${clientId}/campaigns`, { params: { start_date: start || monthStart, end_date: end || today } })
      .then(r => { setData(r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [clientId, start, end]);

  if (loading) return <Loader />;
  const statusBadge = (s: string) => {
    if (s === 'ENABLED') return { text: t('Aktif'), color: T.greenText };
    if (s === 'PAUSED') return { text: t('Durduruldu'), color: T.amberText };
    return { text: s || '—', color: T.textDim };
  };

  const handleExport = () => {
    exportCsv(`x-ads-kampanyalar-${start || ''}-${end || ''}.csv`, data, [
      { key: 'name', label: t('KAMPANYA') },
      { key: 'status', label: t('DURUM') },
      { key: 'spend', label: t('HARCAMA') },
      { key: 'impressions', label: t('GÖSTERİM') },
      { key: 'clicks', label: t('TIKLAMA') },
      { key: 'ctr', label: 'CTR' },
      { key: 'daily_budget', label: t('GÜNLÜK BÜTÇE') },
    ]);
  };

  return (
    <Card pad={0}>
      <div style={{ padding: '16px 24px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' as const }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{t('Kampanyalar')}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ fontSize: 11, color: T.textDim }}>{data.length} {t('kampanya')}</div>
          <Button variant="ghost" color={T.accent} disabled={data.length === 0} onClick={handleExport} style={{ padding: '6px 12px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon.download size={13} />{t('Dışa Aktar')}
          </Button>
        </div>
      </div>
      <div style={{ overflowX: 'auto' as const }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' as const, minWidth: 680 }}>
          <thead style={{ borderBottom: `1px solid ${T.border}`, background: T.card }}>
            <tr>
              {['KAMPANYA', 'DURUM', 'HARCAMA', 'GÖSTERİM', 'TIKLAMA', 'CTR', 'GÜNLÜK BÜTÇE'].map((h, i) => (
                <th key={i} style={{ padding: '10px 14px', fontSize: 9, fontWeight: 700, color: T.textDim, textTransform: 'uppercase' as const, letterSpacing: '0.08em', textAlign: i === 0 ? 'left' as const : 'right' as const, whiteSpace: 'nowrap' as const }}>{t(h)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: '28px 14px', textAlign: 'center' as const, fontSize: 12.5, color: T.textDim }}>{t('Bu dönem için kampanya yok.')}</td></tr>
            ) : data.map((c: any, i: number) => {
              const sb = statusBadge(c.status);
              return (
                <tr key={i} style={{ borderBottom: `1px solid ${T.card}` }} onMouseEnter={e => (e.currentTarget.style.background = T.card)} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  <td style={{ padding: '12px 14px' }}><div style={{ fontSize: 12, fontWeight: 600, color: T.text, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{c.name}</div></td>
                  <td style={{ padding: '12px 14px', textAlign: 'right' as const }}><Badge color={sb.color}>{sb.text}</Badge></td>
                  <td style={{ padding: '12px 14px', textAlign: 'right' as const, fontSize: 12, fontWeight: 600, color: T.text, fontVariantNumeric: 'tabular-nums' }}>{fmt_k(c.spend)}</td>
                  <td style={{ padding: '12px 14px', textAlign: 'right' as const, fontSize: 11, color: T.textMid, fontVariantNumeric: 'tabular-nums' }}>{fmtN(c.impressions)}</td>
                  <td style={{ padding: '12px 14px', textAlign: 'right' as const, fontSize: 11, color: T.textMid, fontVariantNumeric: 'tabular-nums' }}>{fmtN(c.clicks)}</td>
                  <td style={{ padding: '12px 14px', textAlign: 'right' as const, fontSize: 11, color: T.textMid, fontVariantNumeric: 'tabular-nums' }}>{fmtPct(c.ctr)}</td>
                  <td style={{ padding: '12px 14px', textAlign: 'right' as const, fontSize: 11, color: T.textMid, fontVariantNumeric: 'tabular-nums' }}>{fmt_k(c.daily_budget)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// --- MAIN EXPORT ---
export default function XAdsTab({ config, tab = 'overview', start, end }: { config: any; tab?: string; start?: string; end?: string }) {
  const clientId = config?.client_id;
  const accountName = (config?.platforms as any)?.x_ads?.accountName || '';
  const enabled = (config?.platforms as any)?.x_ads?.enabled !== false;
  if (!config?.platforms) return <Loader />;
  if (!clientId || !accountName || !enabled) return <DisconnectedState />;
  if (tab === 'campaigns') return <CampaignsTab clientId={clientId} start={start} end={end} />;
  return <OverviewTab clientId={clientId} start={start} end={end} />;
}
